import { Component, ElementRef, inject, OnInit, ViewChild } from "@angular/core";
import { AgGridAngular } from "ag-grid-angular";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import {
  faArrowRight,
  faDownload,
  faInfoCircle,
  faMailBulk,
  faPhone,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { NgTemplateOutlet } from "@angular/common";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { RecordNavigationComponent } from "../_shared/record-navigation/record-navigation.component";
import { AgGridThemeService } from "../../_services/ag-grid-theme.service";
import { AG_GRID_LOCALE_DE } from "../../_helpers/aggrid.locale.de";
import {
  GridApi,
  RowClassRules,
  SideBarDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-enterprise";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import { ProjectService, StoreService } from "../../_services";
import { ActivatedRoute, RouterLink } from "@angular/router";
import * as XLSX from "xlsx";
import { PAUtil } from "../planning-assistant/classes/util";
import { FullProjectHash } from "../../_models/project.interface";
import { TicketBulkImportParams } from "../../_models/ticket.interface";
import { Priority, Status } from "../../_models";
import { ProjectStoreService } from "../../_services/project-store.service";
import { TooltipModule } from "@cloudfactorydk/ng2-tooltip-directive";
import { ProgressBarComponent } from "../_shared/progress-bar/progress-bar.component";

export interface TicketImportUnit {
  "Externe Auftragsnummer": string | number;
  Status: string;
  Priorität: string;
  Filiale: string;
  Kontaktname: string;
  "Kontakt E-Mail": string;
  "Kontakt Telefon": string;
  "Fehlertext ": string;
  Kurzinformation: string;
  "Datum und Uhrzeit": Date | string;
  Techniker: string;
  Zeit: string;
  Fixtermin: string | number;
  id?: number;
  store_id?: number;
  bulk_id: number;
}

@Component({
  selector: "hawk-project-import-tickets",
  imports: [
    AgGridAngular,
    FaIconComponent,
    ReactiveFormsModule,
    RecordNavigationComponent,
    FormsModule,
    RouterLink,
    NgTemplateOutlet,
    TooltipModule,
    ProgressBarComponent,
  ],
  templateUrl: "./project-import-tickets.component.html",
  styleUrl: "./project-import-tickets.component.scss",
})
export class ProjectImportTicketsComponent implements OnInit {
  public hawkTheme = inject(AgGridThemeService).getTheme();

  protected readonly faPhone = faPhone;
  protected readonly faMailBulk = faMailBulk;
  protected readonly faTrash = faTrash;
  protected readonly faArrowRight = faArrowRight;
  protected readonly Object = Object;
  protected readonly locale = AG_GRID_LOCALE_DE;

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  headers = [
    "Externe Auftragsnummer",
    "Status",
    "Priorität",
    "Filiale",
    "Kontaktname",
    "Kontakt E-Mail",
    "Kontakt Telefon",
    "Fehlertext",
    "Kurzinformation",
    "Datum und Uhrzeit",
    "Techniker",
    "Zeit",
    "Fixtermin",
  ];

  headerFormat: boolean = true;
  addProjectRelations: number[];

  project: FullProjectHash;
  uploadProgress: number;
  showError: string;
  showSuccess: string;
  showFormatModal = false;

  lastUsedBulkId = 0;

  @ViewChild("fileInput") fileInput: ElementRef<HTMLInputElement>;

  file: File;
  ticketImportUnits: TicketImportUnit[] = [];
  importableUnits: TicketImportUnit[] = [];
  alreadyCreatedExternalIds: { id: number; external_order_nr: string }[] = [];
  externalIdsLoaded = false;
  externalIdsLoadingError = false;

  public ticketColDefs: ColDef[] = [
    {
      field: "id",
      headerName: "DB-ID",
      width: 90,
      minWidth: 90,
      cellRenderer: this.idRenderer,
      cellClass: "hover_link_cell",
    },
    {
      field: "Externe Auftragsnummer",
      headerName: "Externe Auftragsnummer",
    },
    {
      field: "Status",
      headerName: "Status (ID)",
    },
    {
      field: "Priorität",
      headerName: "Priorität (ID)",
    },
    {
      field: "Filiale",
      headerName: "Filiale",
    },
    {
      field: "Kontaktname",
      headerName: "Kontaktname",
    },
    {
      field: "Kontakt E-Mail",
      headerName: "E-Mail",
    },
    {
      field: "Kontakt Telefon",
      headerName: "Telefon",
    },
    {
      field: "Fehlertext",
      headerName: "Fehlertext",
      width: 360,
    },
    {
      field: "Kurzinformation",
      headerName: "Kurzinformation",
    },
    {
      field: "Datum und Uhrzeit",
      headerName: "Datum und Uhrzeit",
    },
    {
      field: "Techniker",
      headerName: "Techniker (ID)",
    },
    {
      field: "Zeit",
      headerName: "Zeit",
    },
    {
      field: "Fixtermin",
      headerName: "Fixtermin",
      // Zeigt die Auswertung statt des Rohwerts, damit vor dem Import sichtbar
      // ist, was tatsächlich gesetzt wird.
      valueGetter: (params) => (this.isFixedAppointment(params.data?.["Fixtermin"]) ? "Ja" : "Nein"),
    },
  ];

  private ticketGridApi!: GridApi;
  public rowClassRules: RowClassRules = {
    already_imported: function (params) {
      return params.data.id >= 0;
    },
  };

  public sideBarOptions: SideBarDef = {
    hiddenByDefault: false,
    toolPanels: ["columns", "filters"],
  };

  public defaultColDef: ColDef = {
    filter: true,
  };

  public rowSelection: "single" | "multiple" = "single";
  public status: Status[];
  public priorities: Priority[];
  public projectStoresMap = new Map<string, { id: number }>();
  public allStoresMap = new Map<string, { id: number }>();
  public priorityErrorGroup: FormGroup;
  public statusErrorGroup: FormGroup;
  public nonProjectStores: { name: string; id: number }[] = null;
  public nonExistingStores: { name: string }[] = null;

  constructor(
    private projectService: ProjectService,
    private storeService: StoreService,
    private projectStoreService: ProjectStoreService,
    private route: ActivatedRoute,
  ) {}

  incomingFile(event) {
    this.file = event.target.files[0];
    this.upload();
  }

  upload() {
    const fileReader = new FileReader();
    fileReader.onload = (_) => {
      this.resetData();
      const array_buffer = fileReader.result as ArrayBuffer;
      const data = new Uint8Array(array_buffer);
      const arr = [];
      for (let i = 0; i != data.length; ++i) arr[i] = String.fromCharCode(data[i]);
      const bstr = arr.join("");
      const workbook = XLSX.read(bstr, { type: "binary", cellDates: true });
      const first_sheet_name = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[first_sheet_name];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        header: this.headers,
        dateNF: "yyyy.mm.dd hh:mm",
      });

      const rows = rawRows.slice(this.headerFormat ? 1 : 0).filter((r) => {
        return !!(
          (r["Filiale"] && r["Filiale"].toString().trim()) ||
          (r["Externe Auftragsnummer"] && r["Externe Auftragsnummer"].toString().trim())
        );
      });

      this.setTicketImportUnits(rows as TicketImportUnit[]);
    };
    if (this.file) fileReader.readAsArrayBuffer(this.file);
  }

  normalizeExcelDate(value: string | Date): string | null {
    if (value instanceof Date && !isNaN(value.getTime())) {
      console.log("Formatting Date: " + value.toString());
      return this.formatDate(value);
    }

    if (typeof value === "string") {
      console.log("Formatting string: " + value);
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return this.formatDate(parsed);
      }

      console.log("Doesn't fit default parse format");

      // try german format
      const match_1 = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
      const match_2 = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
      const match = match_1 || match_2;
      if (match) {
        console.log("Matches german format");
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // JS-Monate 0–11
        const year = parseInt(match[3], 10);
        const hour = match[4] ? parseInt(match[4], 10) : 0;
        const minute = match[5] ? parseInt(match[5], 10) : 0;

        const date = new Date(year, month, day, hour, minute);
        if (!isNaN(date.getTime())) {
          return this.formatDate(date);
        }
      }
    }

    return null;
  }

  normalizeBoolField(field_value: string | number): boolean {
    if (!field_value) return false;

    if (typeof field_value == "string") {
      return ["true", "1"].includes(field_value.toLowerCase().replaceAll(" ", ""));
    }

    if (typeof field_value == "number") {
      return field_value == 1;
    }

    return false;
  }

  /** Werte, die in der Fixtermin-Spalte ausdrücklich "kein Fixtermin" bedeuten. */
  private static readonly NO_FIXED_APPOINTMENT_VALUES = ["0", "nein", "no", "false"];

  /**
   * Fixtermin-Spalte der Import-Excel: leer bedeutet kein Fixtermin, jeder
   * andere Inhalt setzt ihn. Ausgenommen sind die üblichen Verneinungen — wer
   * "nein" oder "0" schreibt, meint erkennbar keinen Fixtermin.
   */
  isFixedAppointment(value: string | number): boolean {
    const normalized = value?.toString().trim().toLowerCase();
    if (!normalized) return false;

    return !ProjectImportTicketsComponent.NO_FIXED_APPOINTMENT_VALUES.includes(normalized);
  }

  formatDate(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, "0");

    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
  }

  normalizeExternalOrderNr(value: string | number): string {
    let string_value: string = "";
    if (typeof value === "number") {
      if (value) string_value = value.toString();
    } else if (value && value != "0") {
      string_value = value;
    }

    // if empty return null
    if (!string_value.replaceAll(" ", "")) {
      return null;
    }

    return string_value;
  }

  async setTicketImportUnits(tiu: TicketImportUnit[]): Promise<void> {
    // wait for loading external ids
    while (!this.externalIdsLoadingError && !this.externalIdsLoaded) {
      await PAUtil.sleep(100);
    }

    if (!this.externalIdsLoadingError) {
      tiu.map((unit) => {
        console.log("searching " + unit["Externe Auftragsnummer"]);
        console.log("in " + this.alreadyCreatedExternalIds.map((item) => item.external_order_nr).join(", "));
        const found_ticket = this.alreadyCreatedExternalIds.find(
          (ticket) => ticket.external_order_nr == unit["Externe Auftragsnummer"],
        );
        unit.id = found_ticket?.id;
        unit.bulk_id = this.generateNewBulkId();
      });

      for (const unit of tiu) {
        unit["Externe Auftragsnummer"] = this.normalizeExternalOrderNr(unit["Externe Auftragsnummer"]);
      }

      this.ticketImportUnits = tiu;
      this.importableUnits = tiu.filter((unit) => !(unit.id >= 0));
      this.updateErrors();
    }
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    this.loadProject(id);
    this.loadProjectsPriorities(id);
    this.loadProjectsStatus(id);
    this.loadTicketsExternalOrderNrs(id);

    Promise.all([this.loadProjectsStores(id), this.loadAllStores()]).then(() => {
      console.log("stores loaded");
      this.updateErrors();
    });
  }

  public loadProject(id: string): void {
    this.projectService.getFullProject(id).subscribe(
      (data) => {
        this.project = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  public loadProjectsPriorities(id: string): void {
    this.projectService.getStatuses(id).subscribe(
      (data) => {
        this.status = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  public loadProjectsStatus(id: string): void {
    this.projectService.getPriorities(id).subscribe(
      (data) => {
        this.priorities = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  private loadTicketsExternalOrderNrs(id: string) {
    this.projectService.getTicketsExternalOrderNrs(id).subscribe(
      (data) => {
        this.alreadyCreatedExternalIds = this.alreadyCreatedExternalIds.concat(data);
        this.externalIdsLoaded = true;
      },
      (error) => {
        console.log(error);
        this.externalIdsLoaded = true;
        this.externalIdsLoadingError = true;
        this.showError = "Bestehende Externe Auftragsnummern konnten nicht geladen werden!";
      },
    );
  }

  public ticketQuickSearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.ticketGridApi.setGridOption("quickFilterText", newValue);
  }

  public async loadProjectsStores(id: string): Promise<void> {
    await new Promise<void>((resolve) => {
      this.projectService.getStores(id).subscribe(
        (data) => {
          data.map((item) => {
            for (const key of [item.address_company, item.nr]) {
              if (key) this.projectStoresMap.set(key.toLowerCase().replaceAll(" ", ""), item);
            }
          });
          resolve();
        },
        (error) => {
          console.log(error);
          resolve();
        },
      );
    });
  }

  public async loadAllStores(): Promise<void> {
    await new Promise<void>((resolve) => {
      this.storeService.getStoresAPHashes().subscribe(
        (data) => {
          data.map((item) => {
            for (const key of [item.address_company, item.nr]) {
              if (key) this.allStoresMap.set(key.toLowerCase().replaceAll(" ", ""), item);
            }
          });
          console.log("Set all stores: " + this.allStoresMap.size);
          resolve();
        },
        (error) => {
          console.log(error);
          resolve();
        },
      );
    });
  }

  resetFile() {
    this.file = null;
    this.fileInput.nativeElement.value = "";
    this.resetData();
  }

  resetData(): void {
    this.ticketImportUnits = [];
    this.importableUnits = [];
    this.uploadProgress = null;
    this.statusErrorGroup = null;
    this.priorityErrorGroup = null;
    this.nonProjectStores = null;
    this.nonExistingStores = null;
    this.showError = null;
    this.showSuccess = null;
  }

  onTicketGridReady($event: GridReadyEvent<any>) {
    this.ticketGridApi = $event.api;
  }

  private updateErrors(): void {
    const status_error_group: {} = {};
    const priority_error_group: {} = {};
    const non_project_stores: { name: string; id: number }[] = [];
    const non_existing_stores: { name: string }[] = [];

    this.ticketImportUnits.map((unit) => {
      this.checkValidPriority(unit, priority_error_group);
      this.checkValidStatus(unit, status_error_group);
      this.checkValidStore(unit, non_existing_stores, non_project_stores);
    });

    this.priorityErrorGroup = Object.keys(priority_error_group).length ? new FormGroup(priority_error_group) : null;
    this.statusErrorGroup = Object.keys(status_error_group).length ? new FormGroup(status_error_group) : null;
    this.nonExistingStores =
      this.nonExistingStores || (!this.nonExistingStores && non_existing_stores.length) ? non_existing_stores : null;
    this.nonProjectStores =
      this.nonProjectStores || (!this.nonProjectStores && non_project_stores.length) ? non_project_stores : null;
  }

  checkValidStatus(unit: TicketImportUnit, error_group: {}) {
    const status_id: string = unit["Status"];
    if (!error_group[status_id]) {
      const parsed_status_id = Number.parseInt(status_id);

      let is_invalid = false;
      if (Number.isInteger(parsed_status_id)) {
        is_invalid = !this.status.find((s) => s.id == parsed_status_id);
      } else {
        is_invalid = true;
      }

      if (is_invalid) {
        error_group[status_id] = new FormControl("", [Validators.required]);
      }
    }
  }

  checkValidPriority(unit: TicketImportUnit, error_group: {}) {
    const priority_id: string = unit["Priorität"];
    if (!error_group[priority_id]) {
      const parsed_priority_id = Number.parseInt(priority_id);

      let is_invalid = false;
      if (Number.isInteger(parsed_priority_id)) {
        is_invalid = !this.priorities.find((p) => p.id == parsed_priority_id);
      } else {
        is_invalid = true;
      }

      if (is_invalid) {
        error_group[priority_id] = new FormControl("", [Validators.required]);
      }
    }
  }

  checkValidStore(
    unit: TicketImportUnit,
    not_existing_stores: { name: string }[],
    not_project_stores: { name: string; id: number }[],
  ) {
    const store: string = unit["Filiale"];

    if (!not_existing_stores.find((nes) => nes.name == store)) {
      const store_id = this.findExistingStoreId(store);
      const is_invalid = !store_id;
      if (is_invalid) {
        not_existing_stores.push({ name: store });
      } else {
        if (!not_project_stores.find((nps) => nps.name == store)) {
          const is_invalid = !this.findProjectStoreId(store);
          if (is_invalid) not_project_stores.push({ name: store, id: store_id });
        }
      }
    }
  }

  findProjectStoreId(store: string): number {
    const possible_store = this.projectStoresMap.get(store.toLowerCase().replaceAll(" ", ""));
    return possible_store?.id;
  }

  findExistingStoreId(store: string): number {
    const possible_store = this.allStoresMap.get(store.toLowerCase().replaceAll(" ", ""));
    return possible_store?.id;
  }

  async bulkImportTickets(): Promise<void> {
    const bulk_size = 32;
    const total_bulk = this.generateTicketBulkImportParams();
    const bulk_chunks = PAUtil.chunkArray<TicketBulkImportParams>(total_bulk, bulk_size);

    this.uploadProgress = 0;
    for (const bulk_chunk of bulk_chunks) {
      const got_error = await new Promise<false | string>((resolve) => {
        this.projectService.bulkImportTickets(this.project.id, bulk_chunk).subscribe(
          (ticket_mappings) => {
            for (const mapping of ticket_mappings) {
              this.alreadyCreatedExternalIds.push(mapping.ticket);
              const found_import_unit = this.ticketImportUnits.find((unit) => unit.bulk_id == mapping.bulk_id);
              found_import_unit.id = mapping.ticket.id;
              found_import_unit["Externe Auftragsnummer"] = mapping.ticket.external_order_nr;
            }

            this.ticketImportUnits = [...this.ticketImportUnits];
            this.importableUnits = this.ticketImportUnits.filter((unit) => !(unit.id >= 0));
            resolve(false);
          },
          (error) => {
            console.log(error);
            resolve(error);
          },
        );
      });

      if (got_error) {
        this.showError = got_error;
        break;
      }

      this.uploadProgress = ((bulk_chunks.indexOf(bulk_chunk) + 1) / bulk_chunks.length) * 100;
    }
    this.showSuccess = this.showError ? "" : "Tickets erfolgreich importiert!";
    if (this.showSuccess) {
      setTimeout(() => {
        alert("Tickets erfolgreich importiert! Sie können dieses Fenster jetzt schließen.");
      }, 1000);
    }
  }

  private generateTicketBulkImportParams(): TicketBulkImportParams[] {
    const used_tickets = this.importableUnits;

    return used_tickets.map((unit) => {
      const technician_id = Number.parseInt(unit["Techniker"]);
      const res: TicketBulkImportParams = {
        time_estimation: Number.parseInt(unit["Zeit"]),
        project_id: this.project.id,
        priority_id: this.getMappedPriorityId(unit["Priorität"]),
        status_id: this.getMappedStatusId(unit["Status"]),
        description: unit["Fehlertext"],
        store_id: this.findProjectStoreId(unit["Filiale"]),
        contact_name: unit["Kontaktname"],
        contact_email: unit["Kontakt E-Mail"],
        contact_phone: unit["Kontakt Telefon"],
        external_order_nr: this.normalizeExternalOrderNr(unit["Externe Auftragsnummer"]),
        short_information: unit["Kurzinformation"] || "",
        init_operation: {
          operation_date: this.normalizeExcelDate(unit["Datum und Uhrzeit"]),
          technician_ids: [Number.isInteger(technician_id) ? technician_id : 283],
          date_created: this.normalizeExcelDate(new Date()),
          description: unit["Fehlertext"],
          date_fixed: this.isFixedAppointment(unit["Fixtermin"]),
        },
        bulk_id: unit.bulk_id,
      };
      return res;
    });
  }

  getMappedPriorityId(priority: string): number {
    const controls = this.priorityErrorGroup?.controls;
    if (controls) {
      const priority_control = controls[priority];
      if (priority_control) {
        return priority_control.value;
      }
    }
    return Number.parseInt(priority);
  }

  getMappedStatusId(status: string): number {
    const controls = this.statusErrorGroup?.controls;
    if (controls) {
      const status_control = controls[status];
      if (status_control) {
        return status_control.value;
      }
    }
    return Number.parseInt(status);
  }

  idRenderer(params: any): string {
    const id = params.value;
    if (id >= 0) {
      return `<a class="bm-link" href="/a/ticket/${id}" rel="noopener">${params.value}</a>`;
    } else {
      return "NEU";
    }
  }

  async addAllProjectStores(): Promise<void> {
    for (const update_chunk of PAUtil.chunkArray<{ name: string; id: number }>(this.nonProjectStores, 8)) {
      await Promise.all(
        update_chunk.map(async (store) => {
          return await new Promise<void>((resolve) => {
            this.projectStoreService
              .createProjectStore({
                project_stores_id: this.project.id,
                store_id: store.id,
              })
              .subscribe(
                (_) => {
                  this.projectStoresMap.set(store.name.toLowerCase().replaceAll(" ", ""), { id: store.id });
                  resolve();
                },
                (error) => {
                  console.log(error);
                  resolve();
                },
              );
          });
        }),
      );

      this.updateErrors();
    }
  }

  private generateNewBulkId() {
    return this.lastUsedBulkId++;
  }

  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faDownload = faDownload;

  protected readonly Number = Number;
  protected readonly Math = Math;
}
